import React from "react";
import { Link } from "react-router-dom";
import styles from "./About.module.css";
import {
  FiTarget, FiUsers, FiBriefcase, FiGlobe, FiHeart, FiTrendingUp,
  FiLinkedin, FiTwitter, FiArrowRight, FiStar
} from "react-icons/fi";

const stats = [
  { num: "2023", label: "Founded" },
  { num: "50K+", label: "Active Jobs" },
  { num: "500K+", label: "Job Seekers" },
  { num: "10K+", label: "Companies" },
];

const values = [
  {
    icon: <FiTarget />,
    title: "Mission-Driven",
    desc: "We believe everyone deserves access to meaningful work. Our platform removes barriers and creates equal opportunity for all job seekers.",
  },
  {
    icon: <FiHeart />,
    title: "People-First",
    desc: "Every decision we make starts with people — both job seekers and employers. We build tools that genuinely help humans thrive.",
  },
  {
    icon: <FiTrendingUp />,
    title: "Always Innovating",
    desc: "The job market is always evolving. We invest heavily in technology so our platform stays ahead of the curve.",
  },
  {
    icon: <FiGlobe />,
    title: "Inclusive by Design",
    desc: "We build for diversity and inclusion at every level — from our product to our team to the companies we partner with.",
  },
];

const team = [
  {
    name: "Swapnil Kole",
    role: "Director",
    initial: "SK",
    color: "#5540C7",
    bio: "Serial entrepreneur with a BTech in Computer Science & Engineering from IIIT Bhubaneswar. Has built and scaled 3 tech startups, bringing deep technical expertise and an entrepreneurial mindset to Blockenzyme's vision of transforming recruitment through technology.",
    linkedin: "https://www.linkedin.com/in/swapnil-kole",
    twitter: "#",
  },
  {
    name: "Archana Kole",
    role: "Managing Director",
    initial: "AK",
    color: "#240BAD",
    bio: "A seasoned management professional with a BA (Hons) and extensive experience in leadership and organizational management. Archana drives the company's operational strategy, ensuring seamless day-to-day execution and building a high-performance culture at Blockenzyme.",
    linkedin: "#",
    twitter: "#",
  },
];

const milestones = [
  { year: "2023", title: "Founded in West Bengal", desc: "Blockenzyme Private Limited was incorporated with a mission to modernize recruitment across India using technology." },
  { year: "2023", title: "Platform Development Begins", desc: "Core engineering team assembled and work began on building an intelligent recruitment platform from the ground up." },
  { year: "2024", title: "Beta Launch", desc: "Launched beta version of the platform, onboarding early employers and job seekers to test and refine the product." },
  { year: "2024", title: "Growing Talent Network", desc: "Expanded our candidate and employer base, refining matching algorithms based on real-world hiring data." },
  { year: "2025", title: "Full Platform Launch", desc: "Officially launched the full platform with end-to-end hiring workflows for companies across multiple industries." },
  { year: "2025", title: "Scaling Nationwide", desc: "Began expanding presence across major Indian cities, empowering thousands of job seekers and companies." },
];

const About = () => {
  return (
    <div className={styles.page}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroBadge}>
            <FiStar /> &nbsp; Our Story
          </div>
          <h1 className={styles.heroTitle}>
            Connecting People to <span className={styles.highlight}>Meaningful Work</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Blockenzyme is a next-generation recruitment platform incorporated in West Bengal, built to make hiring smarter, fairer, and faster — for everyone.
          </p>
          <div className={styles.heroBtns}>
            <Link to="/find-jobs" className={styles.primaryBtn}>
              Find Jobs <FiArrowRight />
            </Link>
            <Link to="/hire-employees" className={styles.secondaryBtn}>
              Hire Talent <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className={styles.statsSection}>
        <div className={styles.statsInner}>
          {stats.map((s, i) => (
            <div key={i} className={styles.stat}>
              <div className={styles.statNum}>{s.num}</div>
              <div className={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.missionLayout}>
            <div className={styles.missionLeft}>
              <div className={styles.eyebrow}>Our Mission</div>
              <h2 className={styles.missionTitle}>Democratizing Access to Great Careers</h2>
              <p className={styles.missionText}>
                Blockenzyme was founded in 2023 by entrepreneurs who saw too many talented people struggle to find work that matched their potential — and too many companies unable to find the right people. We built a platform to bridge that gap.
              </p>
              <p className={styles.missionText}>
                Today, we connect over 500,000 job seekers with 10,000+ companies every month, using smart technology to make the process faster, fairer, and more human.
              </p>
              <div className={styles.missionBtns}>
                <Link to="/find-jobs" className={styles.missionBtn}>
                  Explore Jobs <FiArrowRight />
                </Link>
              </div>
            </div>
            <div className={styles.missionRight}>
              <div className={styles.missionVisual}>
                <div className={styles.missionCard} style={{ top: "0", left: "0" }}>
                  <FiBriefcase className={styles.missionCardIcon} />
                  <div className={styles.missionCardNum}>50,000+</div>
                  <div className={styles.missionCardLabel}>Active Jobs</div>
                </div>
                <div className={styles.missionCard} style={{ top: "0", right: "0" }}>
                  <FiUsers className={styles.missionCardIcon} />
                  <div className={styles.missionCardNum}>500K+</div>
                  <div className={styles.missionCardLabel}>Candidates</div>
                </div>
                <div className={styles.missionCard} style={{ bottom: "0", left: "50%", transform: "translateX(-50%)" }}>
                  <FiGlobe className={styles.missionCardIcon} />
                  <div className={styles.missionCardNum}>24+</div>
                  <div className={styles.missionCardLabel}>Cities</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className={styles.valuesSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>What We Stand For</h2>
            <p className={styles.sectionSubtitle}>The values that guide every decision we make</p>
          </div>
          <div className={styles.valuesGrid}>
            {values.map((v, i) => (
              <div key={i} className={styles.valueCard}>
                <div className={styles.valueIcon}>{v.icon}</div>
                <h4 className={styles.valueTitle}>{v.title}</h4>
                <p className={styles.valueDesc}>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Our Journey</h2>
            <p className={styles.sectionSubtitle}>From a bold idea in 2023 to a growing recruitment platform across India</p>
          </div>
          <div className={styles.timeline}>
            {milestones.map((m, i) => (
              <div key={i} className={`${styles.timelineItem} ${i % 2 === 0 ? styles.timelineLeft : styles.timelineRight}`}>
                <div className={styles.timelineContent}>
                  <div className={styles.timelineYear}>{m.year}</div>
                  <h4 className={styles.timelineTitle}>{m.title}</h4>
                  <p className={styles.timelineDesc}>{m.desc}</p>
                </div>
                <div className={styles.timelineDot} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className={styles.teamSection}>
        <div className={styles.sectionInner}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Our Leadership</h2>
            <p className={styles.sectionSubtitle}>The leadership driving Blockenzyme's vision</p>
          </div>
          <div className={styles.teamGrid}>
            {team.map((member, i) => (
              <div key={i} className={styles.teamCard}>
                <div
                  className={styles.teamAvatar}
                  style={{ background: member.color + "18", color: member.color }}
                >
                  {member.initial}
                </div>
                <h4 className={styles.teamName}>{member.name}</h4>
                <p className={styles.teamRole}>{member.role}</p>
                <p className={styles.teamBio}>{member.bio}</p>
                <div className={styles.teamSocials}>
                  <a href={member.linkedin} className={styles.socialLink}>
                    <FiLinkedin />
                  </a>
                  <a href={member.twitter} className={styles.socialLink}>
                    <FiTwitter />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>Join Our Mission</h2>
          <p className={styles.ctaSubtitle}>
            Whether you're looking for your next opportunity or your next great hire — we're here to help.
          </p>
          <div className={styles.ctaBtns}>
            <Link to="/find-jobs" className={styles.ctaPrimaryBtn}>
              Find a Job <FiArrowRight />
            </Link>
            <Link to="/hire-employees" className={styles.ctaSecondaryBtn}>
              Start Hiring <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
